import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { Tooltip } from "@itandi/itandi-bb-ui/Tooltip";
import { Controller, useFormContext } from "react-hook-form";
import { MaintenanceCompanyCombobox } from "~/components/specifics/vendors/deals/index/search/detail/MaintenanceCompanyCombobox";
import { VendorPartnerCombobox } from "~/components/specifics/vendors/deals/index/search/detail/VendorPartnerCombobox";
import { VendorUserCombobox } from "~/components/specifics/vendors/deals/index/search/detail/VendorUserCombobox";
import type { DealCompanySetting } from "~/models/CompanySetting";
import type { DealSearchCondition } from "~/models/Deal";
import { ApplicationTypeCheckBox } from "./detail/ApplicationTypeCheckBox";
import { DealTypeCheckBox } from "./detail/DealTypeCheckBox";
import { SearchConditionDate } from "./detail/SearchConditionDate";
import { StatusCombobox } from "./detail/StatusCombobox";

type Props = {
	maintenanceCompanies: DealCompanySetting[] | null;
};

export function DealSearchDetailTab({ maintenanceCompanies }: Props) {
	const { register } = useFormContext<DealSearchCondition>();

	return (
		<FormGroup variant="vertical">
			<FormControl label="未読を優先表示する">
				<Controller
					name="prioritySort"
					render={({ field: { value, onChange, ...field } }) => (
						<InputCheckBox
							{...field}
							checked={!!value}
							label="未読を優先表示する"
							onChange={(e) => {
								onChange(e.target.checked);
							}}
						/>
					)}
				/>
			</FormControl>
			<FormControl label="状況(ステータス)">
				<StatusCombobox />
			</FormControl>
			<FormControl label="状況(申請関連)">
				<Flex marginY={8} wrap="wrap">
					<ApplicationTypeCheckBox
						applicationType="cancel"
						key="cancel"
						label="取下申請あり"
					/>
					<ApplicationTypeCheckBox
						applicationType="requestEstimation"
						key="requestEstimation"
						label="見積申請中"
					/>
					<ApplicationTypeCheckBox
						applicationType="draftEstimation"
						key="draftEstimation"
						label="見積編集中"
					/>
					<ApplicationTypeCheckBox
						applicationType="requestEstimationByCompany"
						key="requestEstimationByCompany"
						label="見積申請あり"
					/>
					<Flex alignItems="center">
						<Tooltip>
							管理会社により見積りが申請されている案件に絞り込みます
						</Tooltip>
					</Flex>
				</Flex>
			</FormControl>
			<FormControl label="案件タイプ">
				<Flex marginY={8} wrap="wrap">
					<DealTypeCheckBox
						dealType="restoration"
						key="restoration"
						label="原状回復"
					/>
					<DealTypeCheckBox dealType="repair" key="repair" label="修繕" />
					<DealTypeCheckBox dealType="other" key="other" label="その他" />
				</Flex>
			</FormControl>
			<FormControl label="物件名">
				<Input {...register("name")} placeholder="六本木マンション" />
			</FormControl>
			<FormControl label="部屋番号">
				<Input {...register("roomNumber")} placeholder="101" />
			</FormControl>
			<FormControl label="所在地">
				<Input {...register("address")} placeholder="東京都港区六本木1-2-3" />
			</FormControl>
			<FormControl label="入居者">
				<Input {...register("tenantName")} placeholder="内装 太郎" />
			</FormControl>
			<FormControl label="担当者">
				<VendorUserCombobox />
			</FormControl>
			<FormControl label="管理会社">
				<MaintenanceCompanyCombobox
					maintenanceCompanies={maintenanceCompanies}
				/>
			</FormControl>
			<FormControl label="実施会社">
				<VendorPartnerCombobox />
			</FormControl>
			<FormControl label="更新日">
				<Flex alignItems="center">
					<SearchConditionDate attributeName="updatedMinDate" />
					〜
					<SearchConditionDate attributeName="updatedMaxDate" />
				</Flex>
			</FormControl>
			<FormControl label="立会日程">
				<Flex alignItems="center">
					<SearchConditionDate attributeName="inspectionMinDate" />
					〜
					<SearchConditionDate attributeName="inspectionMaxDate" />
				</Flex>
			</FormControl>
			<FormControl label="現地調査日程">
				<Flex alignItems="center">
					<SearchConditionDate attributeName="surveyMinDate" />
					〜
					<SearchConditionDate attributeName="surveyMaxDate" />
				</Flex>
			</FormControl>
			<FormControl label="見積提出日">
				<Flex alignItems="center">
					<SearchConditionDate attributeName="sendEstimateMinDate" />
					〜
					<SearchConditionDate attributeName="sendEstimateMaxDate" />
				</Flex>
				<FormControlText help="ステータスを「見積確認中」に進めた日" />
			</FormControl>
			<FormControl label="発注日">
				<Flex alignItems="center">
					<SearchConditionDate attributeName="orderMinDate" />
					〜
					<SearchConditionDate attributeName="orderMaxDate" />
				</Flex>
				<FormControlText help="ステータスを「工事完了待ち」に進めた日" />
			</FormControl>
			<FormControl label="工期開始日">
				<Flex alignItems="center">
					<SearchConditionDate attributeName="constructionMinStartDate" />
					〜
					<SearchConditionDate attributeName="constructionMaxStartDate" />
				</Flex>
			</FormControl>
			<FormControl label="工期終了日">
				<Flex alignItems="center">
					<SearchConditionDate attributeName="constructionMinEndDate" />
					〜
					<SearchConditionDate attributeName="constructionMaxEndDate" />
				</Flex>
			</FormControl>
			<FormControl label="検収依頼日">
				<Flex alignItems="center">
					<SearchConditionDate attributeName="constructionReportedMinDate" />
					〜
					<SearchConditionDate attributeName="constructionReportedMaxDate" />
				</Flex>
				<FormControlText help="ステータスを「検収中」に進めた日" />
			</FormControl>
			<FormControl label="完了日">
				<Flex alignItems="center">
					<SearchConditionDate attributeName="completedMinDate" />
					〜
					<SearchConditionDate attributeName="completedMaxDate" />
				</Flex>
			</FormControl>
			<FormControlText help="ステータスを「完了」に進めた日" />
			<FormControl label="登録日">
				<Flex alignItems="center">
					<SearchConditionDate attributeName="registrationMinDate" />
					〜
					<SearchConditionDate attributeName="registrationMaxDate" />
				</Flex>
			</FormControl>
		</FormGroup>
	);
}
